import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { ArrowRight, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import api, { LOCATIONS, PROJECT_TYPES, trackEvent } from '@/lib/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const STEPS = ['Project Basics', 'Space & Layout', 'Lifestyle', 'Budget', 'Goals & Inspiration', 'Contact & Submit'];

const STYLE_OPTIONS = ['Contemporary', 'Modern Minimalist', 'Cape Dutch', 'Industrial', 'Tropical Modern', 'Farmhouse', 'Mediterranean', 'African Contemporary'];
const SPECIAL_SPACES = ['Home Office', 'Gym', 'Wine Cellar', 'Home Theatre', 'Pool', 'Outdoor Kitchen', 'Staff Quarters', 'Double Garage', 'Guest Suite', 'Garden Studio'];
const PRIORITIES = ['Natural Light', 'Energy Efficiency', 'Security', 'Privacy', 'Indoor-Outdoor Flow', 'Views', 'Low Maintenance', 'Sustainability', 'Entertainment', 'Family-Friendly'];

export default function BriefBuilder() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [briefId, setBriefId] = useState(null);
  const [form, setForm] = useState({
    project_type: '', location: '', timeline: '',
    bedrooms: '', bathrooms: '', special_spaces: [],
    family_size: '', entertaining: '', work_from_home: false,
    budget_range: '', budget_flexibility: '',
    priorities: [], must_haves: [], style_preferences: [],
    additional_notes: '', email: '', name: '', phone: '',
  });

  useEffect(() => { trackEvent('page_view', { page: 'brief_builder' }); }, []);

  const update = (key, value) => setForm(p => ({ ...p, [key]: value }));
  const toggleArray = (key, value) => {
    setForm(p => ({
      ...p, [key]: p[key].includes(value) ? p[key].filter(v => v !== value) : [...p[key], value]
    }));
  };

  const canNext = () => {
    if (step === 0) return form.project_type && form.location;
    if (step === 5) return form.email;
    return true;
  };

  const handleSubmit = async () => {
    if (!form.email) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
        bathrooms: form.bathrooms ? Number(form.bathrooms) : null,
        family_size: form.family_size ? Number(form.family_size) : null,
      };
      const { data } = await api.post('/brief/save', payload);
      setBriefId(data.brief_id);
      setSubmitted(true);
      trackEvent('brief_submitted', { project_type: form.project_type });
      toast.success('Brief submitted successfully!');
    } catch { toast.error('Failed to submit brief'); }
    finally { setLoading(false); }
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  if (submitted) {
    return (
      <div className="min-h-screen bg-brand-light pt-20" data-testid="brief-success">
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="font-heading text-3xl font-medium text-brand-dark mb-4">Brief Submitted</h1>
          <p className="text-brand-dark/60 font-body text-base mb-2">Your project brief has been saved and linked to your profile.</p>
          <p className="text-brand-dark/40 font-body text-sm mb-8">Reference: {briefId}</p>
          <p className="text-brand-dark/50 font-body text-sm">Our team will review your brief and reach out within 24-48 hours to discuss next steps.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-light pt-20" data-testid="brief-builder-page">
      <div className="section-dark py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-brand-accent font-heading text-xs font-bold uppercase tracking-[0.2em] mb-3">Interactive Tool</p>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tighter font-medium text-white mb-4">Turn Ideas Into a Clear Brief</h1>
          <p className="text-white/50 font-body text-base max-w-2xl">
            Structure your thinking so an architect can respond meaningfully. Most clients arrive with a vision but not yet a brief. This changes that.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-heading uppercase tracking-wider text-brand-dark/50">Step {step + 1} of {STEPS.length}</span>
            <span className="text-xs font-heading uppercase tracking-wider text-brand-accent-dark font-semibold">{STEPS[step]}</span>
          </div>
          <Progress value={progress} className="h-1.5" data-testid="brief-progress" />
        </div>

        <div className="bg-white rounded-sm border border-black/10 p-8" data-testid="brief-form">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>

              {/* Step 0: Project Basics */}
              {step === 0 && (
                <div className="space-y-6">
                  <h2 className="font-heading text-xl font-medium">Project Basics</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60">Project Type *</Label>
                      <Select value={form.project_type} onValueChange={(v) => update('project_type', v)}>
                        <SelectTrigger data-testid="brief-select-type" className="rounded-sm h-11 mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{PROJECT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60">Location *</Label>
                      <Select value={form.location} onValueChange={(v) => update('location', v)}>
                        <SelectTrigger data-testid="brief-select-location" className="rounded-sm h-11 mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{LOCATIONS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60">Desired Timeline</Label>
                    <Select value={form.timeline} onValueChange={(v) => update('timeline', v)}>
                      <SelectTrigger data-testid="brief-select-timeline" className="rounded-sm h-11 mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asap">As soon as possible</SelectItem>
                        <SelectItem value="3_months">Within 3 months</SelectItem>
                        <SelectItem value="6_months">Within 6 months</SelectItem>
                        <SelectItem value="12_months">Within 12 months</SelectItem>
                        <SelectItem value="exploring">Just exploring</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Step 1: Space & Layout */}
              {step === 1 && (
                <div className="space-y-6">
                  <h2 className="font-heading text-xl font-medium">Space & Layout</h2>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60">Bedrooms</Label>
                      <Input data-testid="brief-input-bedrooms" type="number" value={form.bedrooms} onChange={(e) => update('bedrooms', e.target.value)} placeholder="e.g. 4" className="rounded-sm h-11 mt-1.5" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60">Bathrooms</Label>
                      <Input data-testid="brief-input-bathrooms" type="number" value={form.bathrooms} onChange={(e) => update('bathrooms', e.target.value)} placeholder="e.g. 3" className="rounded-sm h-11 mt-1.5" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-3 block">Special Spaces</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {SPECIAL_SPACES.map(space => (
                        <label key={space} className="flex items-center gap-2 cursor-pointer" data-testid={`brief-space-${space.toLowerCase().replace(/\s/g, '-')}`}>
                          <Checkbox checked={form.special_spaces.includes(space)} onCheckedChange={() => toggleArray('special_spaces', space)} />
                          <span className="text-sm font-body">{space}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Lifestyle */}
              {step === 2 && (
                <div className="space-y-6">
                  <h2 className="font-heading text-xl font-medium">Lifestyle</h2>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60">Family Size</Label>
                      <Input data-testid="brief-input-family" type="number" value={form.family_size} onChange={(e) => update('family_size', e.target.value)} placeholder="e.g. 4" className="rounded-sm h-11 mt-1.5" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60">Entertaining Style</Label>
                      <Select value={form.entertaining} onValueChange={(v) => update('entertaining', v)}>
                        <SelectTrigger data-testid="brief-select-entertaining" className="rounded-sm h-11 mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="minimal">Minimal</SelectItem>
                          <SelectItem value="regular">Regular small gatherings</SelectItem>
                          <SelectItem value="frequent">Frequent large events</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="brief-wfh-checkbox">
                    <Checkbox checked={form.work_from_home} onCheckedChange={(c) => update('work_from_home', c)} />
                    <span className="text-sm font-body">I work from home and need a dedicated office space</span>
                  </label>
                </div>
              )}

              {/* Step 3: Budget */}
              {step === 3 && (
                <div className="space-y-6">
                  <h2 className="font-heading text-xl font-medium">Budget</h2>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60">Budget Range</Label>
                    <Select value={form.budget_range} onValueChange={(v) => update('budget_range', v)}>
                      <SelectTrigger data-testid="brief-select-budget" className="rounded-sm h-11 mt-1.5"><SelectValue placeholder="Select range" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="under_2m">Under R2 million</SelectItem>
                        <SelectItem value="2m_5m">R2M - R5M</SelectItem>
                        <SelectItem value="5m_10m">R5M - R10M</SelectItem>
                        <SelectItem value="10m_20m">R10M - R20M</SelectItem>
                        <SelectItem value="over_20m">Over R20M</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60">Budget Flexibility</Label>
                    <Select value={form.budget_flexibility} onValueChange={(v) => update('budget_flexibility', v)}>
                      <SelectTrigger data-testid="brief-select-flexibility" className="rounded-sm h-11 mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed - cannot exceed</SelectItem>
                        <SelectItem value="some">Some flexibility (10-15%)</SelectItem>
                        <SelectItem value="flexible">Flexible for the right design</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Step 4: Goals & Inspiration */}
              {step === 4 && (
                <div className="space-y-6">
                  <h2 className="font-heading text-xl font-medium">Goals & Inspiration</h2>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-3 block">Priorities (select top 3-5)</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {PRIORITIES.map(p => (
                        <label key={p} className="flex items-center gap-2 cursor-pointer" data-testid={`brief-priority-${p.toLowerCase().replace(/\s/g, '-')}`}>
                          <Checkbox checked={form.priorities.includes(p)} onCheckedChange={() => toggleArray('priorities', p)} />
                          <span className="text-sm font-body">{p}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-3 block">Style Preferences</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {STYLE_OPTIONS.map(s => (
                        <label key={s} className="flex items-center gap-2 cursor-pointer" data-testid={`brief-style-${s.toLowerCase().replace(/\s/g, '-')}`}>
                          <Checkbox checked={form.style_preferences.includes(s)} onCheckedChange={() => toggleArray('style_preferences', s)} />
                          <span className="text-sm font-body">{s}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60">Additional Notes</Label>
                    <Textarea data-testid="brief-notes" value={form.additional_notes} onChange={(e) => update('additional_notes', e.target.value)} placeholder="Anything else you'd like us to know..." className="rounded-sm mt-1.5 min-h-[100px]" />
                  </div>
                </div>
              )}

              {/* Step 5: Contact */}
              {step === 5 && (
                <div className="space-y-6">
                  <h2 className="font-heading text-xl font-medium">Your Details</h2>
                  <p className="text-brand-dark/50 text-sm font-body">Enter your contact details so we can send you a copy and follow up.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60">Full Name</Label>
                      <Input data-testid="brief-contact-name" value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Your name" className="rounded-sm h-11 mt-1.5" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60">Email *</Label>
                      <Input data-testid="brief-contact-email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@email.com" className="rounded-sm h-11 mt-1.5" required />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60">Phone</Label>
                      <Input data-testid="brief-contact-phone" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="+27 ..." className="rounded-sm h-11 mt-1.5" />
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-black/5">
            <Button
              data-testid="brief-prev-btn"
              variant="ghost"
              onClick={() => setStep(s => s - 1)}
              disabled={step === 0}
              className="text-brand-dark/60 hover:text-brand-dark font-body text-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                data-testid="brief-next-btn"
                onClick={() => setStep(s => s + 1)}
                disabled={!canNext()}
                className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-sm font-semibold uppercase tracking-wider disabled:opacity-40"
              >
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                data-testid="brief-submit-btn"
                onClick={handleSubmit}
                disabled={loading || !form.email}
                className="bg-brand-accent text-brand-dark hover:bg-brand-accent/90 rounded-sm font-body text-sm font-semibold uppercase tracking-wider disabled:opacity-40"
              >
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing...</> : <>Complete Your Brief <CheckCircle className="w-4 h-4 ml-2" /></>}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
